import PropTypes from 'prop-types';
import React from 'react';
import api from '../../../Api';
import makeConnectTo from '../../util/makeConnectTo';
import { connectToCollaborativeLists } from '../../CollaborativeLists/containers/CollaborativeListsDataStore';
import { message } from 'antd';
import { connectToUser } from '../../User/containers/UserDataStore';
import combineConnectors from '../../util/combineConnectors';
import { isFunction } from 'lodash';
import { withLocalize } from '../../Localization/withLocalize';

const CommunitiesContext = React.createContext();

const name = 'communitiesDataStore';

export const useCommunities = () => React.useContext(CommunitiesContext);

export const connectToCommunities = makeConnectTo(CommunitiesContext, name);

export class CommunitiesDataStore extends React.Component {
	static propTypes = {
		children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
	};

	joinCommunityMessageTimeInSeconds = 5;

	state = {
		circles: [],
		communities: [],
		genreCommunities: [],
		isLoading: true,
	};

	render() {
		const communitiesDataStore = this.getStore();
		return (
			<CommunitiesContext.Provider value={communitiesDataStore}>
				{typeof this.props.children === 'function'
					? this.props.children(communitiesDataStore)
					: this.props.children}
			</CommunitiesContext.Provider>
		);
	}

	componentDidMount() {
		this.fetchCommunities();
	}

	componentWillUnmount() {
		if (this.communitiesFetch && this.communitiesFetch.cancel) {
			this.communitiesFetch.cancel();
		}
	}

	getStore = (state = this.state) => ({
		state,
		isLoading: state.isLoading,
		refresh: () => this.refreshCommunities(),
		refreshWithLoadingState: () =>
			this.refreshCommunitiesWithLoadingState(),
		joinCommunity: (affiliationId, onSuccess, onFail) =>
			this.joinCommunity(affiliationId, onSuccess, onFail),
		joinValidatedCommunity: (affiliationId, code, onSuccess) =>
			this.joinValidatedCommunity(affiliationId, code, onSuccess),
		leaveCommunity: (affiliationId, type) =>
			this.leaveCommunity(affiliationId, type),
		leaveValidatedCommunity: (affiliationId, code) =>
			this.leaveValidatedCommunity(affiliationId, code),
		getCommunityById: communityId => this.getCommunityById(communityId),
		getCommunitiesInvitedTo: () => this.getCommunitiesInvitedTo(),
		getOpenCommunities: () => this.getOpenCommunities(),
		getEligibilityForJoiningCommunity: communityId =>
			this.getEligibilityForJoiningCommunity(communityId),
		getCommunities: () => this.getCommunities(),
		createCommunity: (values, onSuccess) =>
			this.createCommunity(values, onSuccess),
		deleteCommunity: (communityId, onSuccess) =>
			this.deleteCommunity(communityId, onSuccess),
		updateCommunity: (communityId, values, onSuccess) =>
			this.updateCommunity(communityId, values, onSuccess),
		getGenreCommunities: () => this.getGenreCommunities(),
	});

	refreshCommunities = () => {
		this.fetchCommunities();
		return this.communitiesFetch.promise;
	};

	refreshCommunitiesWithLoadingState = async () => {
		this.setState({
			isLoading: true,
		});
		await this.refreshCommunities();
	};

	refreshListsAndUsersJoinedCommunities = () => {
		this.props.collaborativeListsDataStore.refresh();
		this.props.userDataStore.fetchJoinedCommunities();
	};

	fetchCommunities = () => {
		const isAnonymous = this.props.userDataStore.isAnonymous();
		const communitiesIdentifier = isAnonymous ? 'genres' : 'me';
		const request = api.cancelableGet(
			`/api/v1/affiliations/${communitiesIdentifier}`,
		);
		request.promise
			.then(
				isAnonymous
					? this.cacheGenreCommunitiesApiResponse
					: this.cacheApiResponse,
			)
			.finally(() => this.setState({ isLoading: false }));
		this.communitiesFetch = request;
	};

	cacheGenreCommunitiesApiResponse = apiResponse => {
		const genreCommunities =
			apiResponse && this.buildCommunities(apiResponse);
		this.setState({
			genreCommunities: genreCommunities || [],
		});
	};

	cacheApiResponse = apiResponse => {
		const communities = apiResponse && this.buildCommunities(apiResponse);
		this.setState({
			communities:
				communities &&
				communities.sort(
					(a, b) =>
						new Date(b.lastViewedDate) - new Date(a.lastViewedDate),
				),
			circles: (apiResponse && apiResponse.affiliationCircles) || [],
		});
	};

	buildCommunities = apiResponse => {
		const {
			affiliations,
			lastViewedByAffiliationId,
			browseRefinements,
			userAvatarsByAppUserId,
			usersById,
			organizationsById,
			groupValidationItems,
		} = apiResponse;
		return affiliations.map(affiliation => {
			const { ownerIds } = affiliation;
			const owners = ownerIds.map(ownerId =>
				this.getOwnerInfo(
					ownerId,
					userAvatarsByAppUserId[ownerId],
					usersById[ownerId] &&
						usersById[ownerId].orgID &&
						organizationsById[
							usersById[ownerId].orgID.toLowerCase()
						],
				),
			);

			const lastViewedDate =
				lastViewedByAffiliationId &&
				affiliation.id in lastViewedByAffiliationId
					? lastViewedByAffiliationId[affiliation.id]
					: null;
			return {
				...affiliation,
				groupValidationItems:
					groupValidationItems &&
					groupValidationItems.filter(
						gvi => gvi.affiliationId === affiliation.id,
					),
				lastViewedDate,
				browseRefinements:
					browseRefinements &&
					browseRefinements.filter(
						br => br.viewId === affiliation.viewId,
					),
				owners,
				owner: owners && owners[0],
			};
		});
	};

	getCommunities = () => {
		const { isAnonymous } = this.props.userDataStore;
		const { communities, genreCommunities } = this.state;
		return isAnonymous() ? genreCommunities : communities;
	};

	getOwnerInfo = (appUserId, userAvatar, organization) => {
		var owner = {};
		owner.userAvatar = userAvatar && userAvatar;
		owner.organization =
			organization && organization.name ? organization.name : '';
		owner.appUserId = appUserId;
		return owner;
	};

	getCommunityTypeString = typeNumber => {
		if (typeNumber === 1) {
			return 'organization';
		} else {
			return 'user';
		}
	};

	joinCommunity = (affiliationId, onSuccess, onFail) => {
		if (!affiliationId) {
			return this.displayUnableToLoadCommunityMessage();
		}

		const community = this.getCommunityById(affiliationId);

		if (!community) {
			return this.displayUnableToLoadCommunityMessage();
		}

		if (community.member) {
			return this.displayAlreadyMemberOfCommunityMessage(community.name);
		}

		const typeString = this.getCommunityTypeString(community.type);

		const joinCommunityUrl =
			process.env.REACT_APP_API_URL +
			`/api/v1/affiliations/me/${affiliationId}/${typeString}`;

		fetch(joinCommunityUrl, {
			method: 'PUT',
			headers: { 'Access-Control-Allow-Origin': '*' },
			credentials: 'include',
			mode: 'cors',
		})
			.then(response => {
				if (response.status === 200) {
					this.refreshCommunities();
					this.refreshListsAndUsersJoinedCommunities();
					typeof onSuccess === 'function' && onSuccess();
					return this.displaySuccessfullyJoinedCommunityMessage(
						community.name,
					);
				}
				typeof onFail === 'function' && onFail();
				this.displayUnableToJoinCommunityMessage();
			})
			.catch(() => {
				this.displayErrorJoiningCommunityMessage();
			});
	};

	createCommunity = async (values, onSuccess) => {
		const endpoint = `/api/v1/affiliations`;
		const {
			userDataStore: { fetchJoinedCommunitiesWithNoLoadingState },
		} = this.props;
		try {
			let response = await api.post(endpoint, values);
			await this.refreshCommunitiesWithLoadingState();
			fetchJoinedCommunitiesWithNoLoadingState();
			isFunction(onSuccess) && onSuccess(response.id);
		} catch (e) {
			this.onCatch(e);
		}
	};

	deleteCommunity = async (communityId, onSuccess) => {
		const endpoint = `/api/v1/affiliations/${communityId}`;
		const {
			collaborativeListsDataStore: { refresh: refreshCollabLists },
			userDataStore: { fetchJoinedCommunitiesWithNoLoadingState },
		} = this.props;
		try {
			await api.del(endpoint);
			isFunction(onSuccess) && onSuccess();
			this.refreshCommunitiesWithLoadingState();
			fetchJoinedCommunitiesWithNoLoadingState();
			refreshCollabLists();
		} catch (e) {
			this.onCatch(e);
		}
	};

	updateCommunity = async (communityId, values, onSuccess) => {
		const {
			userDataStore: { fetchJoinedCommunitiesWithNoLoadingState },
		} = this.props;
		const endpoint = `/api/v1/affiliations/${communityId}/doNotUpdateValidation`;
		try {
			await api.put(endpoint, values);
			await this.refreshCommunities();
			fetchJoinedCommunitiesWithNoLoadingState();
			isFunction(onSuccess) && onSuccess();
		} catch (e) {
			this.onCatch(e);
		}
	};

	onCatch = e => {
		const { translate } = this.props;
		console.error(e);
		message.error(translate('there_was_an_error_with_your_request'));
	};

	displayUnableToLoadCommunityMessage = () => {
		message.warn(
			<span>Unable to load the community you are trying to join.</span>,
			this.joinCommunityMessageTimeInSeconds,
		);
	};

	displayAlreadyMemberOfCommunityMessage = communityName => {
		message.warn(
			<span>
				You are already a member of the <b>{communityName}</b>{' '}
				community.
			</span>,
			this.joinCommunityMessageTimeInSeconds,
		);
	};

	displaySuccessfullyJoinedCommunityMessage = communityName => {
		message.success(
			<span>
				You've successfully joined the <b>{communityName}</b> community.
			</span>,
			this.joinCommunityMessageTimeInSeconds,
		);
	};

	displayErrorJoiningCommunityMessage = () => {
		message.error(
			<span>An error occurred while trying to join the community.</span>,
			this.joinCommunityMessageTimeInSeconds,
		);
	};

	displayUnableToJoinCommunityMessage = () => {
		message.warn(
			<span>You are unable to join this community.</span>,
			this.joinCommunityMessageTimeInSeconds,
		);
	};

	getEligibilityForJoiningCommunity = communityId => {
		const openCommunities = this.getOpenCommunities();
		const communitiesInvitedTo = this.getCommunitiesInvitedTo();
		const isCommunity = comm => comm.id === Number(communityId);
		return (
			openCommunities.some(isCommunity) ||
			communitiesInvitedTo.some(isCommunity)
		);
	};

	getGenreCommunities = () => {
		const { communities } = this.state;
		return communities.filter(comm => comm.isGenreCommunity);
	};

	getGenreCommunitiesForAnonUser = () => {
		const { genreCommunities } = this.state;
		return genreCommunities.length ? genreCommunities : [];
	};

	getMyOpenCommunities = () => {
		const { communities } = this.state;
		return communities.length
			? communities.filter(
					c =>
						!c.member &&
						!c.groupValidationItems.find(
							gvi => gvi.type !== 'group',
						),
			  )
			: [];
	};

	getOpenCommunities = () => {
		const {
			userDataStore: { isAnonymous },
		} = this.props;
		return isAnonymous()
			? this.getGenreCommunitiesForAnonUser()
			: this.getMyOpenCommunities();
	};

	getCommunitiesInvitedTo = () => {
		const communities = this.state.communities;
		return communities.length
			? communities.filter(
					c =>
						!c.member &&
						c.groupValidationItems.find(
							gvi => gvi.type !== 'group',
						),
			  )
			: [];
	};

	getCommunityById = communityId => {
		const communities = this.getCommunities();
		return communities.find(
			community => Number(community.id) === Number(communityId),
		);
	};

	leaveCommunity = (affiliationId, type) => {
		const typeString = this.getCommunityTypeString(type);

		api.del(`/api/v1/affiliations/me/${affiliationId}/${typeString}`)
			.then(() => {
				message.success('You have left the community');
				this.refreshCommunities();
				this.refreshListsAndUsersJoinedCommunities();
			})
			.catch(this.onCatch);
	};

	joinValidatedCommunity = (affiliationId, code, onSuccess) => {
		const { translate } = this.props;
		if (code !== '') {
			api.put(`/api/v1/affiliations/${affiliationId}/validate/${code}`)
				.then(result => {
					if (result === 0) {
						message.error(
							translate('unable_to_validate_community_code', {
								supportEmail: 'support@abovethetreeline.com',
							}),
						);
					} else {
						message.success(
							translate(
								'join_community_with_validation_success_message',
							),
						);
						typeof onSuccess === 'function' && onSuccess();
						this.refreshCommunities();
						this.refreshListsAndUsersJoinedCommunities();
					}
				})
				.catch(this.onCatch);
		}
	};

	leaveValidatedCommunity = affiliationId => {
		api.del(`/api/v1/affiliations/${affiliationId}/validate`)
			.then(() => {
				message.success(<span>You have left the community</span>);
				this.refreshCommunities();
				this.refreshListsAndUsersJoinedCommunities();
			})
			.catch(this.onCatch);
	};
}

const connectors = [connectToCollaborativeLists, connectToUser];

export default combineConnectors(connectors)(
	withLocalize(CommunitiesDataStore),
);
